import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Spinner, Toast } from '../../lib';
import apiServices from '../../services/apiServices';
import {
  GetSupportMemberHistory,
  SupportMemberHistoryResponse,
} from '../../services/dtos';
import {
  TICKET_QUEUES,
  TicketAgeDot,
  TicketCategoryBadge,
  TicketEscalatedBadge,
  TicketStatusBadge,
  TicketUrgentBadge,
  fmtDateTime,
  roleLabel,
} from './supportShared';

// S1: a member's running record (vision Feature 6) — "if someone's had three
// wage discrepancies at the same venue, that pattern is worth seeing." The
// server returns only tickets THIS viewer could work from the queue side, so
// confidential tickets drop out exactly as on the board.
const SupportMemberHistoryPage: React.FC = () => {
  const { id } = useParams();

  const [history, setHistory] = useState<SupportMemberHistoryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setHistory(null);
    setError(null);
    apiServices.client
      .get(new GetSupportMemberHistory({ id }))
      .then(setHistory)
      .catch((e: any) =>
        setError(e?.responseStatus?.message || 'Loading the history failed — try again.')
      );
  }, [id]);

  // The pattern the vision names: repeat wage tickets at one venue.
  const wagePatterns = useMemo(() => {
    const wage = (history?.tickets || []).filter(
      (t) => t.category === 'WageDiscrepancy' && t.status !== 'Dismissed'
    );
    const byVenue = new Map<number, { site: string; count: number }>();
    for (const t of wage) {
      if (t.employerIdSnapshot == null) continue;
      const cur = byVenue.get(t.employerIdSnapshot);
      byVenue.set(t.employerIdSnapshot, {
        site: cur?.site || t.siteSnapshot || `employer #${t.employerIdSnapshot}`,
        count: (cur?.count || 0) + 1,
      });
    }
    return [...byVenue.values()]
      .filter((v) => v.count >= 2)
      .sort((a, b) => b.count - a.count);
  }, [history]);

  const tickets = history?.tickets || [];

  return (
    <div className="mx-auto w-full flex max-w-6xl flex-col gap-6 dark:text-white">
      <div>
        <Link
          to="/help/queue"
          className="text-sm text-indigo-600 dark:text-indigo-400"
        >
          ← Back to the queue
        </Link>
        {history && (
          <>
            <h2 className="mt-1 text-2xl font-bold break-words">{history.name}</h2>
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
              <span>Member #{history.uniqueMemberId}</span>
              {history.membershipStatus && <span>{history.membershipStatus}</span>}
              <span>
                {tickets.length} request{tickets.length === 1 ? '' : 's'} on record
              </span>
            </div>
          </>
        )}
      </div>

      {error && <Toast color="failure">{error}</Toast>}
      {!history && !error && <Spinner color="info" />}

      {wagePatterns.length > 0 && (
        <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900 p-4 text-sm text-amber-800 dark:text-amber-200">
          <span className="font-medium">Pattern:</span>{' '}
          {wagePatterns
            .map((p) => `${p.count} wage tickets at ${p.site}`)
            .join('; ')}
          . Repeat wage issues at one venue are worth a look — and useful
          evidence if this ever becomes formal.
        </div>
      )}

      {history && tickets.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-8 text-center text-sm text-gray-500 dark:text-gray-400">
          No requests from this member that you can see.
        </div>
      )}

      {tickets.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-3 py-2 w-14">#</th>
                <th className="px-3 py-2">Subject</th>
                <th className="px-3 py-2 w-40">Type</th>
                <th className="px-3 py-2 w-40">Where it sits</th>
                <th className="px-3 py-2 w-48">Related show</th>
                <th className="px-3 py-2 w-28">Status</th>
                <th className="px-3 py-2 w-32">Waiting</th>
                <th className="px-3 py-2 w-40">Opened</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-900">
              {tickets.map((t) => (
                <tr key={t.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-3 py-2 text-gray-400 text-xs">{t.id}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to={`/help/ticket/${t.id}`}
                        className="font-medium text-indigo-600 dark:text-indigo-400 break-words"
                      >
                        {t.subject}
                      </Link>
                      <TicketUrgentBadge isUrgent={t.isUrgent} />
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <TicketCategoryBadge category={t.category} />
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300">
                    {t.escalatedToRole ? (
                      <TicketEscalatedBadge toRole={t.escalatedToRole} />
                    ) : (
                      TICKET_QUEUES.find((q) => q.name === t.queue)?.label ||
                      roleLabel(t.queue)
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 break-words">
                    {t.showSnapshot
                      ? [t.showSnapshot, t.siteSnapshot].filter(Boolean).join(' — ')
                      : t.relatedShowFreeText || '—'}
                  </td>
                  <td className="px-3 py-2">
                    <TicketStatusBadge status={t.status} />
                  </td>
                  <td className="px-3 py-2">
                    <TicketAgeDot ticket={t} />
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                    {fmtDateTime(t.createdAtUtc)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SupportMemberHistoryPage;
